// ...existing code...
/**
 * API Agent: Handles all communication with the backend.
 * Manages fetch requests, error handling, and response parsing.
 */

// In local development, use relative URLs so the React proxy (package.json "proxy")
// forwards /api requests to http://localhost:8000 — avoiding CORS issues.
// In production (Cloud Run + nginx proxy), same-origin relative URLs work too.
export const API_BASE_URL = (() => {
  const configured = process.env.REACT_APP_API_URL;
  if (configured && configured.trim().length > 0) {
    return configured.trim();
  }

  return '';
})();

/**
 * Global 403 permission-denied event name.
 *
 * When any API call returns 403 with a backend entitlement message
 * (`Requires permission: <perm>` / `Requires permissions: <perms>`, raised by
 * `backend/app/dependencies/permissions.py`), {@link APIAgent.request} dispatches
 * a `CustomEvent(PERMISSION_DENIED_EVENT, { detail: { permission, message } })`
 * on `window`. A single top-level listener in `App.js` opens the
 * `UpgradePromptModal` from this event, so a stale-cache miss the backend
 * rejected surfaces the upgrade pitch instead of a raw error.
 *
 * The request still rejects as normal (callers' try/catch is unchanged) — the
 * event is an additive side-channel. Non-entitlement 403s (admin checks,
 * internal-auth) carry different detail text and never fire this event.
 */
export const PERMISSION_DENIED_EVENT = 'billing:permission-denied';

/** Backend entitlement-denial messages start with this prefix (see permissions.py). */
const PERMISSION_DENIED_PREFIX = 'Requires permission';

/**
 * Parse the permission code out of a backend entitlement-denial detail string.
 * Handles both the single (`Requires permission: offer:create`) and multi
 * (`Requires permissions: a, b`) variants, returning the first code.
 * @param {string} detail
 * @returns {string|null}
 */
function parseDeniedPermission(detail) {
  if (typeof detail !== 'string') return null;
  const idx = detail.indexOf(':');
  if (idx === -1) return null;
  const first = detail.slice(idx + 1).split(',')[0];
  return first ? first.trim() : null;
}

/**
 * Dispatch the global permission-denied event for an entitlement 403. Safe to
 * call in environments without `window` (SSR / some test setups) — it no-ops.
 * @param {string} detail - The parsed backend error detail.
 */
function emitPermissionDenied(detail) {
  if (typeof window === 'undefined' || typeof window.dispatchEvent !== 'function') {
    return;
  }
  const permission = parseDeniedPermission(detail);
  try {
    window.dispatchEvent(
      new CustomEvent(PERMISSION_DENIED_EVENT, {
        detail: { permission, message: detail },
      })
    );
  } catch (_) {
    /* CustomEvent unsupported (very old env) — degrade silently. */
  }
}

class APIAgent {
  static async delay(ms) {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  /**
   * Send a request to the backend
   * @param {string} endpoint - API endpoint
   * @param {string} method - HTTP method (GET, POST, etc.)
   * @param {object} data - Request payload
   * @param {object} customHeaders - Additional headers to include
   * @returns {Promise<object|null>} Response data; `null` for a 204 No Content
   *
   * A `FormData` payload is sent as-is (multipart) and without the JSON
   * Content-Type, so the browser can set the multipart boundary itself. Uploads
   * then get the same auth, structured-error and timeout handling as JSON calls.
   */
  static async request(endpoint, method = 'GET', data = null, customHeaders = {}, { skipAuthRedirect = false, timeout = 10000, retries = 0, retryDelayMs = 300 } = {}) {
    let attempt = 0;
    const isFormData = typeof FormData !== 'undefined' && data instanceof FormData;

    while (true) {
      const controller = new AbortController();
      const timerId = setTimeout(() => controller.abort(), timeout);
      try {
        const options = {
          method,
          credentials: 'include',
          signal: controller.signal,
          headers: isFormData
            ? { ...customHeaders }
            : {
              'Content-Type': 'application/json',
              ...customHeaders,
            },
        };

        if (data && (method === 'POST' || method === 'PUT' || method === 'PATCH')) {
          options.body = isFormData ? data : JSON.stringify(data);
        }

        const response = await fetch(`${API_BASE_URL}${endpoint}`, options);

        if (!response.ok) {
          if (response.status === 401) {
            // Never auto-redirect for auth endpoints — the login form needs to
            // surface its own error message instead of triggering a page reload
            // that re-hydrates the previous session and creates a login loop.
            const isAuthEndpoint = endpoint.startsWith('/api/auth/');
            if (!skipAuthRedirect && !isAuthEndpoint) {
              // Clear any stale cached user so the reloaded app doesn't briefly
              // render the previous session before /me rejects the cookie.
              try {
                localStorage.removeItem('cutthe6.com_auth_user');
                localStorage.removeItem('cutthe6.com_user_id');
              } catch (_) { /* ignore storage errors */ }
              // Straight to sign-in: "/" is the public landing page now.
              window.location.href = '/login';
            }
            // Try to surface the backend's actual error detail (e.g. "Invalid
            // email or password") rather than a generic "session expired".
            let authDetail = 'Session expired — please log in again.';
            try {
              const body = await response.json();
              if (body && typeof body.detail === 'string') {
                authDetail = body.detail;
              }
            } catch (_) { /* no json body */ }
            const authError = new Error(authDetail);
            authError.status = response.status;
            authError.statusCode = response.status;
            throw authError;
          }
          let detail = `${response.status} ${response.statusText}`;
          // The parsed `detail` exactly as the backend sent it. Structured
          // errors (e.g. the RECR completion 422, which carries
          // `{message, problems[]}`) are only actionable in their original
          // shape: stringifying them here is what the *message* is for, and a
          // caller that wants to render per-item problems needs the object.
          // Kept separate from `detail` so no existing `err.message` consumer
          // changes behaviour.
          let rawDetail;
          try {
            const body = await response.json();
            if (body.detail) {
              rawDetail = body.detail;
              // FastAPI 422 returns detail as an array of validation errors;
              // coerce to a readable string instead of "[object Object]".
              if (typeof body.detail === 'string') {
                detail = body.detail;
              } else if (Array.isArray(body.detail)) {
                detail = body.detail
                  .map((e) => e?.msg || JSON.stringify(e))
                  .join('; ');
              } else if (typeof body.detail.message === 'string') {
                // Structured backend error: the human-readable half is
                // `message`. Without this the whole JSON blob ends up rendered
                // verbatim wherever `err.message` is shown to a user.
                detail = body.detail.message;
              } else {
                detail = JSON.stringify(body.detail);
              }
            }
          } catch (_) { /* no json body */ }
          // Global gating backstop: a 403 carrying a backend entitlement
          // message means the frontend gate let a call through that the backend
          // rejected. Surface the upgrade prompt via a window event; the request
          // still rejects normally below so existing callers are unaffected.
          if (response.status === 403 && typeof detail === 'string' && detail.startsWith(PERMISSION_DENIED_PREFIX)) {
            emitPermissionDenied(detail);
          }
          const apiError = new Error(detail);
          apiError.status = response.status;
          apiError.statusCode = response.status;
          apiError.detail = rawDetail;
          throw apiError;
        }

        // 204 No Content (e.g. a DELETE) has no body to parse.
        if (response.status === 204) {
          return null;
        }
        return await response.json();
      } catch (error) {
        if (error.name === 'AbortError') {
          if (method === 'GET' && attempt < retries) {
            attempt += 1;
            console.warn(`API request timed out for ${endpoint}; retrying (${attempt}/${retries})`);
            if (retryDelayMs > 0) {
              await this.delay(retryDelayMs);
            }
            continue;
          }

          console.error(`API request timed out for ${endpoint}`);
          // Flag it so callers can tell "we gave up waiting" apart from "the
          // server said no". A client-side abort says nothing about whether
          // the write landed — callers that can re-check server state should.
          const timeoutError = new Error('Request timed out. Please check your connection.');
          timeoutError.isTimeout = true;
          throw timeoutError;
        }
        console.error(`API request failed for ${endpoint}:`, error);
        throw error;
      } finally {
        clearTimeout(timerId);
      }
    }
  }

  /**
   * Fetch data from the root endpoint
   * @returns {Promise<object>} Root endpoint data
   */
  static async getRootData() {
    return this.request('/');
  }

  /**
   * Send a request to the LangChain endpoint
   * @param {string} text - User input text
   * @returns {Promise<object>} LangChain response
   */
  static async sendToLangChain(text) {
    return this.request('/langchain', 'POST', { text });
  }

  /**
   * Analyze a property
   * @param {string} address - Property address
   * @returns {Promise<object>} Analysis results
   */
  static async analyzeProperty(address) {
    return this.request('/api/property/analyze', 'POST', { address });
  }

  /**
   * Compare markets
   * @param {Array<string>} locations - List of locations
   * @returns {Promise<object>} Market comparison results
   */
  static async compareMarkets(locations) {
    return this.request('/api/market/compare', 'POST', { locations });
  }

  /**
   * Get investment insights
   * @param {Array<object>} properties - List of properties
   * @returns {Promise<object>} Investment insights
   */
  static async getInvestmentInsights(properties) {
    return this.request('/api/investment/insights', 'POST', { properties });
  }

  /**
   * Fetch all houses with keyset pagination.
   * @param {number|null} cursor - Last house id from previous page (null for first page)
   * @param {number} limit - Number of records to return
   * @param {boolean} includeTotal - Whether to request total_count from backend
   * @returns {Promise<{houses: Array, total_count: number|null, next_cursor: number|null}>}
   */
  static async getHouses(cursor = null, limit = 12, includeTotal = false) {
    try {
      const params = new URLSearchParams({ limit });
      if (cursor != null) params.set('cursor', cursor);
      params.set('include_total', includeTotal ? 'true' : 'false');
      const data = await this.request(
        `/api/houses?${params}`,
        'GET',
        null,
        {},
        { timeout: 30000, retries: 1, retryDelayMs: 400 }
      );
      if (data && typeof data.total_count === 'number') {
        return data;
      }
      const houses = Array.isArray(data) ? data : data.houses || [];
      return {
        houses,
        total_count: typeof data?.total_count === 'number' ? data.total_count : null,
        next_cursor: data?.next_cursor ?? null,
      };
    } catch (error) {
      console.error('Error fetching houses:', error);
      throw error;
    }
  }

  /**
   * Fetch houses by criteria with pagination.
   * @param {object} criteria - Search criteria
   * @param {number} skip - Number of records to skip
   * @param {number} limit - Number of records to return
   * @param {boolean} includeTotal - Set false on page 2+ to skip the COUNT query
   * @returns {Promise<{houses: Array, total_count: number|null}>} Paginated results
   */
  static async getHousesByCriteria(criteria = {}, skip = 0, limit = 12, includeTotal = true) {
    try {
      const params = new URLSearchParams();

      Object.entries(criteria).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          params.append(key, value);
        }
      });

      params.append('skip', skip);
      params.append('limit', limit);
      params.append('include_total', includeTotal);

      const queryString = params.toString();
      const endpoint = `/api/houses/search/by-criteria?${queryString}`;

      const data = await this.request(
        endpoint,
        'GET',
        null,
        {},
        { timeout: 30000, retries: 1, retryDelayMs: 400 }
      );
      if (data && (typeof data.total_count === 'number' || data.total_count === null)) {
        return data;
      }
      const houses = Array.isArray(data) ? data : [];
      return { houses, total_count: houses.length };
    } catch (error) {
      console.error('Error searching houses by criteria:', error);
      throw error;
    }
  }

  /**
   * Run semantic search for houses only.
   * @param {string} query - Natural language query
   * @param {number} limit - Maximum results
   * @param {number} threshold - Similarity threshold
   * @returns {Promise<Array>} Ranked house results
   */
  static async semanticSearchHouses(query, limit = 10, threshold = 0.35) {
    try {
      const data = await this.request(
        '/api/search/semantic',
        'POST',
        {
          query,
          search_type: 'house',
          limit,
          threshold,
        },
        {},
        { timeout: 60000 }
      );

      return Array.isArray(data)
        ? data
            .filter((result) => result.type === 'house')
            .map((result) => ({
              id: result.id,
              score: result.score,
              ...result.data,
            }))
        : [];
    } catch (error) {
      console.error('Error running semantic house search:', error);
      throw error;
    }
  }

  /**
   * Fetch house by ID
   * @param {number} id - House ID
   * @returns {Promise<object>} House data
   */
  static async getHouseById(id) {
    return this.request(`/api/houses/${id}`);
  }

  /**
   * Create a new house listing
   * @param {object} houseData - House data matching HouseCreate schema
   * @param {object} options - Request options
   * @returns {Promise<object>} Created house
   */
  static async createHouse(houseData, options = {}) {
    return this.request('/api/houses/', 'POST', houseData, {}, {
      timeout: 60000,
      ...options,
    });
  }

  /**
   * Get all listings for a seller
   * @param {number} sellerId - Seller person ID
   * @returns {Promise<{total_count: number, houses: object[]}>} Seller listings
   */
  static async getSellerListings(sellerId) {
    const params = new URLSearchParams({ include_total: 'false' });
    return this.request(`/api/houses/seller/${sellerId}?${params.toString()}`);
  }

  /**
   * Update house details (address, price, description, etc.)
   * @param {number} houseId - House ID
   * @param {object} data - Fields to update
   * @param {object} options - Request options
   * @returns {Promise<object>} Updated house
   */
  static async updateHouse(houseId, data, options = {}) {
    return this.request(`/api/houses/${houseId}`, 'PUT', data, {}, {
      timeout: 60000,
      ...options,
    });
  }

  /**
   * Update listing status (draft, active, pending, sold, withdrawn)
   * @param {number} houseId - House ID
   * @param {string} status - New status
   * @returns {Promise<object>} Updated house
   */
  static async updateListingStatus(houseId, status) {
    return this.request(`/api/houses/${houseId}/status?status=${encodeURIComponent(status)}`, 'PUT');
  }

  /**
   * Upload images for a house.
   *
   * Files are uploaded in small batches rather than in a single request. A
   * single request with many large photos can exceed the proxy body-size limit
   * (causing a 413) or the request timeout. The backend appends each batch to
   * the house's existing images, so sequential batches accumulate correctly.
   *
   * @param {number} houseId - House ID
   * @param {FileList|File[]} files - Image files to upload
   * @param {object} options - Request options
   * @param {number} [options.timeout=60000] - Per-batch timeout in ms
   * @param {number} [options.batchSize=4] - Files per request
   * @returns {Promise<{image_urls: string[], uploaded: number}>} Upload result
   */
  static async uploadHouseImages(houseId, files, options = {}) {
    const timeout = options.timeout ?? 60000;
    const batchSize = options.batchSize ?? 4;
    const allFiles = Array.from(files);

    let imageUrls = [];
    let uploaded = 0;

    for (let i = 0; i < allFiles.length; i += batchSize) {
      const batch = allFiles.slice(i, i + batchSize);
      const result = await this._uploadHouseImageBatch(houseId, batch, timeout);
      // Each response returns the full cumulative list, so the last one wins.
      imageUrls = result.image_urls ?? imageUrls;
      uploaded += result.uploaded ?? batch.length;
    }

    return { image_urls: imageUrls, uploaded };
  }

  /**
   * Upload a single batch of image files for a house.
   * @private
   */
  static async _uploadHouseImageBatch(houseId, files, timeout) {
    const controller = new AbortController();
    const timerId = setTimeout(() => controller.abort(), timeout);

    try {
      const formData = new FormData();
      for (const file of files) {
        formData.append('files', file);
      }

      const response = await fetch(`${API_BASE_URL}/api/houses/${houseId}/images`, {
        method: 'POST',
        credentials: 'include',
        signal: controller.signal,
        body: formData,
      });

      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.detail || `Upload failed: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      if (error.name === 'AbortError') {
        console.error(`API request timed out for /api/houses/${houseId}/images`);
        throw new Error('Request timed out. Please check your connection.');
      }
      console.error('Error uploading house images:', error);
      throw error;
    } finally {
      clearTimeout(timerId);
    }
  }

  /**
   * Delete a house image by index
   * @param {number} houseId - House ID
   * @param {number} imageIndex - Index of image to delete
   * @returns {Promise<{image_urls: string[], deleted: string}>} Delete result
   */
  static async deleteHouseImage(houseId, imageIndex) {
    return this.request(`/api/houses/${houseId}/images/${imageIndex}`, 'DELETE');
  }

  /**
   * Get the full URL for a house image path
   * @param {string} imagePath - Relative image path from API
   * @returns {string} Full URL
   */
  static getImageUrl(imagePath) {
    if (!imagePath) return '';
    if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) {
      return imagePath;
    }
    return `${API_BASE_URL}${imagePath}`;
  }

  /**
   * Get health status
   * @returns {Promise<object>} Health status
   */
  static async getHealth() {
    return this.request('/health');
  }

  /**
   * Get a person by ID
   * @param {number} personId - Person ID
   * @returns {Promise<object>} Person data
   */
  static async getPerson(personId) {
    return this.request(`/api/people/${personId}`);
  }

  /**
   * Update a person's profile (marital_status, spouse_id, etc.)
   * @param {number} personId - Person ID
   * @param {object} data - Fields to update
   * @returns {Promise<object>} Updated person
   */
  static async updatePerson(personId, data) {
    return this.request(`/api/people/${personId}`, 'PUT', data);
  }

  /**
   * Search people by email for spouse linking
   * @param {string} email - Email to search
   * @returns {Promise<Array>} Matching people
   */
  static async searchPeopleByEmail(email) {
    return this.request(`/api/people/?email=${encodeURIComponent(email)}`);
  }

  /**
   * Preview the seller listing agreement for a house.
   * Returns the rendered HTML, template metadata, and the SHA-256 the seller
   * must echo back at sign time.
   * @param {number} houseId
   * @returns {Promise<object>} { template_id, template_version, template_sha256, rendered_html, consent_version, disclaimers }
   */
  static async previewListingAgreement(houseId) {
    const data = await this.request(
      '/api/listing-agreements/preview',
      'POST',
      { house_id: houseId },
      {},
      { timeout: 30000 },
    );

    return {
      ...data,
      rendered_html: data?.rendered_html ?? data?.html ?? '',
      template_sha256: data?.template_sha256 ?? data?.sha256 ?? '',
    };
  }

  /**
   * Initiate DocuSign embedded signing for the listing agreement.
   *
   * Replaces the old local typed-name `signListingAgreement` flow. The seller
   * must have read the in-product preview, acknowledged the disclaimers, and
   * checked the ESIGN consent box before this is called. The backend creates
   * (or reuses) the DocuSign envelope and returns an embedded recipient-view
   * URL the browser is then redirected to.
   *
   * @param {number} houseId
   * @param {object} payload - { consent_version, doc_sha256 } echoed from /preview
   *   so the backend mid-flow template-change guard stays intact.
   * @returns {Promise<object>} { document_id, signature_id, status, envelope_id, signing_url, expires_in }
   */
  static async initiateListingAgreement(houseId, { consent_version, doc_sha256 } = {}) {
    return this.request(
      '/api/listing-agreements/initiate',
      'POST',
      { house_id: houseId, consent_version, doc_sha256 },
      {},
      { timeout: 30000 },
    );
  }

  /**
   * Reconcile the listing-agreement envelope status after DocuSign redirects
   * the seller back to the return route. The backend reads the live envelope
   * state (getEnvelope) and finalizes the document if completed. Idempotent —
   * safe to call repeatedly while polling for completion.
   *
   * @param {number} houseId
   * @param {number} [documentId] - advisory; backend resolves the latest doc.
   * @returns {Promise<object>} status payload (same shape as getListingAgreementStatus)
   */
  static async reconcileListingAgreement(houseId, documentId) {
    const body = { house_id: houseId };
    if (documentId != null) body.document_id = documentId;
    const data = await this.request(
      '/api/listing-agreements/reconcile',
      'POST',
      body,
      {},
      { timeout: 30000 },
    );
    return {
      ...data,
      has_signed_agreement: data?.has_signed_agreement ?? data?.status === 'signed',
    };
  }

  /**
   * Get the listing-agreement status for a house. Surfaces the full set of
   * DocuSign-aware states: none | sent | in_progress | signed | voided.
   *
   * `has_signed_agreement` is kept as a derived boolean (true only on `signed`)
   * for backward compatibility with the publish gate, which keys on COMPLETED.
   *
   * @param {number} houseId
   * @returns {Promise<object>} {
   *   status, has_signed_agreement, current_template_version, signed_at?,
   *   signer_name?, template_version?, document_id?, download_url?,
   *   envelope_id?, signature_id?
   * }
   */
  static async getListingAgreementStatus(houseId) {
    const data = await this.request(`/api/houses/${houseId}/listing-agreement`);
    return {
      ...data,
      has_signed_agreement: data?.has_signed_agreement ?? data?.status === 'signed',
    };
  }

  /**
   * Fetch the unsigned listing agreement as a PDF — the document the seller
   * will sign in DocuSign — so they can print or save it before signing.
   *
   * @param {number} houseId
   * @returns {Promise<{blob: Blob, filename: string|null}>}
   *   Rejects with `err.status` set (403 not the seller, 404 no listing,
   *   503 the PDF can't be generated right now).
   */
  static async getListingAgreementPreviewPdf(houseId) {
    return this.fetchBlob(`/api/houses/${houseId}/listing-agreement/preview-pdf`, {
      timeout: 30000,
      accept: 'application/pdf',
    });
  }

  /**
   * Export selected listings to MLS-shaped JSON or CSV.
   *
   * The backend returns:
   *   JSON format: { data: [...], warnings: [...], disclaimer } as application/json.
   *   CSV format:  text/csv; export warnings in the X-Export-Warnings header.
   *
   * For the JSON path we surface the warnings directly to the toast and
   * generate a client-side blob download for the records. For the CSV
   * path the backend returns ``text/csv``; any export warnings arrive in
   * the ``X-Export-Warnings`` response header as a JSON array.
   *
   * @param {object} payload - { listing_ids: number[], format: 'json' | 'csv' }
   * @returns {Promise<object>} {
   *   warnings: [{ listing_id, listing_number, missing_required: string[] }],
   * }
   */
  static async exportMlsListings({ listing_ids, format = 'json' } = {}) {
    if (!Array.isArray(listing_ids) || listing_ids.length === 0) {
      throw new Error('Select at least one listing to export.');
    }

    const endpoint = '/api/admin/listings/export-mls';

    if (format === 'csv') {
      // CSV path: fetch raw bytes and offer a blob download. The backend
      // returns text/csv with a Content-Disposition filename header.
      const response = await fetch(`${API_BASE_URL}${endpoint}`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ listing_ids, format: 'csv' }),
      });
      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        const err = new Error(body.detail || `Export failed: ${response.status}`);
        err.status = response.status;
        throw err;
      }
      const blob = await response.blob();
      const ts = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 16);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const cd = response.headers.get('Content-Disposition') || '';
      const match = cd.match(/filename="([^"]+)"/i);
      a.download = (match && match[1]) ? match[1] : `mls-export-${ts}.csv`;
      a.click();
      URL.revokeObjectURL(url);

      // Warnings travel in the X-Export-Warnings header as a JSON array.
      // If the payload exceeds ~7 KB the backend truncates the header and
      // appends # WARNING: rows to the CSV body instead.
      const warningsHeader = response.headers.get('X-Export-Warnings');
      let warnings = [];
      if (warningsHeader) {
        try {
          warnings = JSON.parse(warningsHeader);
        } catch (_) {
          /* ignore */
        }
      }
      return { warnings };
    }

    // JSON path — read records + warnings, then offer a JSON blob download.
    const result = await this.request(
      endpoint,
      'POST',
      { listing_ids, format: 'json' },
      {},
      { timeout: 60000 },
    );

    const records = Array.isArray(result?.data) ? result.data : [];
    const warnings = Array.isArray(result?.warnings) ? result.warnings : [];
    const disclaimer = typeof result?.disclaimer === 'string' ? result.disclaimer : undefined;

    const ts = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 16);
    const blob = new Blob([JSON.stringify({ disclaimer, records, warnings }, null, 2)], {
      type: 'application/json',
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `mls-export-${ts}.json`;
    a.click();
    URL.revokeObjectURL(url);

    return { warnings };
  }

  /**
   * GET a file (a PDF, an image) and hand it to the browser as a download.
   *
   * Same cookie auth as `request()`. A non-2xx response rejects with an Error
   * carrying `status` and the backend's raw `detail` (so `{code, message}`
   * errors stay actionable), and `message` set to the readable half.
   *
   * @param {string} endpoint - API path, e.g. `/api/contingency-notices/7/document`
   * @param {string} fallbackFilename - Used when there is no Content-Disposition filename
   * @param {{ timeout?: number }} [options]
   * @returns {Promise<{ filename: string, size: number }>}
   */
  static async download(endpoint, fallbackFilename, { timeout = 30000 } = {}) {
    const { blob, filename: served } = await this.fetchBlob(endpoint, { timeout });
    const filename = served || fallbackFilename;
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
    return { filename, size: blob.size };
  }

  /**
   * Fetch a file (e.g. a PDF) as a Blob, for the caller to show or save.
   *
   * Same cookie auth and error shape as `download()`: a non-2xx response
   * rejects with an Error carrying `status` and the backend's raw `detail`.
   * A 401 does NOT redirect to sign-in; the caller decides what to do.
   *
   * @param {string} endpoint - API path, e.g. `/api/houses/7/disclosure/report`
   * @param {{ timeout?: number, accept?: string }} [options]
   * @returns {Promise<{ blob: Blob, filename: string|null }>}
   */
  static async fetchBlob(endpoint, { timeout = 30000, accept } = {}) {
    const controller = new AbortController();
    const timerId = setTimeout(() => controller.abort(), timeout);
    try {
      const response = await fetch(`${API_BASE_URL}${endpoint}`, {
        method: 'GET',
        credentials: 'include',
        signal: controller.signal,
        ...(accept ? { headers: { Accept: accept } } : {}),
      });
      if (!response.ok) {
        let rawDetail;
        let message = `${response.status} ${response.statusText}`;
        try {
          const body = await response.json();
          rawDetail = body?.detail;
          if (typeof rawDetail === 'string') message = rawDetail;
          else if (rawDetail && typeof rawDetail.message === 'string') message = rawDetail.message;
        } catch (_) { /* no json body */ }
        const err = new Error(message);
        err.status = response.status;
        err.statusCode = response.status;
        err.detail = rawDetail;
        throw err;
      }
      const blob = await response.blob();
      const disposition = (response.headers && response.headers.get('Content-Disposition')) || '';
      const match = disposition.match(/filename="([^"]+)"/i);
      return { blob, filename: (match && match[1]) || null };
    } catch (error) {
      if (error.name === 'AbortError') {
        const timeoutError = new Error('Request timed out. Please check your connection.');
        timeoutError.status = 0;
        throw timeoutError;
      }
      throw error;
    } finally {
      clearTimeout(timerId);
    }
  }
}

export default APIAgent;
